import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CustomThemeProvider } from './context/ThemeContext';
import { ErrorProvider } from './context/ErrorContext';
import ErrorDialog from './components/ErrorDialog';
import { LoadingProvider } from './context/LoadingContext';
import LoadingSpinner from './components/LoadingSpinner';
import { OrganizationProvider } from './context/OrganizationContext';
import { NotificationProvider } from './context/NotificationContext';
import Layout from './components/Layout'; // Eager load Layout

// Lazy-loaded and prefetched components
const Welcome = lazy(() => import(/* webpackPrefetch: true */ './components/Welcome'));
const Home = lazy(() => import(/* webpackPrefetch: true */ './pages/Home'));
const AgentList = lazy(() => import(/* webpackPrefetch: true */ './pages/AgentList'));
const UserList = lazy(() => import(/* webpackPrefetch: true */ './pages/UserList'));
const AgentForm = lazy(() => import(/* webpackPrefetch: true */ './pages/AgentForm'));
const AgentChat = lazy(() => import(/* webpackPrefetch: true */ './pages/AgentChat'));
const Connections = lazy(() => import(/* webpackPrefetch: true */ './pages/Connections'));
const ToolsRepository = lazy(() => import(/* webpackPrefetch: true */ './pages/ToolsRepository'));
const Help = lazy(() => import(/* webpackPrefetch: true */ './pages/Help'));
const ConversationHistory = lazy(() => import(/* webpackPrefetch: true */ './pages/ConversationHistory'));
const UserForm = lazy(() => import(/* webpackPrefetch: true */ './pages/UserForm'));
const ExternalConnectionSetup = lazy(() => import(/* webpackPrefetch: true */ './pages/ExternalConnectionSetup'));
const NotAuthorized = lazy(() => import(/* webpackPrefetch: true */ './pages/NotAuthorized'));
const RoleList = lazy(() => import(/* webpackPrefetch: true */ './pages/RoleList'));
const RoleForm = lazy(() => import(/* webpackPrefetch: true */ './pages/RoleForm'));
const Quotas = lazy(() => import(/* webpackPrefetch: true */ './pages/Quotas'));
const ToolCreate = lazy(() => import(/* webpackPrefetch: true */ './pages/ToolCreate'));
const ToolEdit = lazy(() => import(/* webpackPrefetch: true */ './pages/ToolEdit'));
const PublicChat = lazy(() => import(/* webpackPrefetch: true */ './pages/PublicChat'));
const Dashboard = lazy(() => import(/* webpackPrefetch: true */ './pages/Dashboard'));
const WhatsAppPage = lazy(() => import(/* webpackPrefetch: true */ './pages/WhatsApp'));
const TemplateForm = lazy(() => import(/* webpackPrefetch: true */ './pages/WhatsApp/TemplateForm'));

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Home />} />
    <Route path="/agents" element={<AgentList />} />
    <Route path="/agents/:agentId/edit" element={<AgentForm />} />
    <Route path="/agents/:agentId/chat" element={<AgentChat />} />
    <Route path="/agents/:agentId/conversations" element={<ConversationHistory />} />
    <Route path="/agents/new" element={<AgentForm />} />
    <Route path="/connections" element={<Connections />} />
    <Route path="/whatsapp" element={<WhatsAppPage />} />
    <Route path="/whatsapp/templates/new" element={<TemplateForm />} />
    <Route path="/whatsapp/templates/edit/:templateId" element={<TemplateForm />} />
    <Route path="/tools" element={<ToolsRepository />} />
    <Route path="/tools/new" element={<ToolCreate />} />
    <Route path="/tools/:toolId/edit" element={<ToolEdit />} />
    <Route path="/help" element={<Help />} />
    <Route path="/users" element={<UserList />} />
    <Route path="/users/new" element={<UserForm />} />
    <Route path="/users/:userId/edit" element={<UserForm />} />
    <Route path="/roles" element={<RoleList />} />
    <Route path="/roles/new" element={<RoleForm />} />
    <Route path="/roles/:roleId/edit" element={<RoleForm />} />
    <Route path="/quotas" element={<Quotas />} />
    <Route path="/dashboard" element={<Dashboard />} />
  </Routes>
);

function App() {
  const [googleIdToken, setGoogleIdToken] = useState<string | null>(localStorage.getItem('googleIdToken'));

  const isTokenInvalid = async (token: string) => {
    try {
      const jwtDecode = (await import('jwt-decode')).default;
      const decoded: any = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) {
        return true;
      }
      return false;
    } catch (error) {
      return true;
    }
  };

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem('googleIdToken');
      if (token && (await isTokenInvalid(token))) {
        handleSignOutSuccess();
      }
    };
    checkToken();
  }, []);

  useEffect(() => {
    const handleAuthError = () => {
      handleSignOutSuccess();
    };

    window.addEventListener('auth-error', handleAuthError);

    return () => {
      window.removeEventListener('auth-error', handleAuthError);
    };
  }, []);

  const handleSignInSuccess = (token: string) => {
    setGoogleIdToken(token);
    // Here you might also want to send this token to your backend for verification
  };

  const handleSignOutSuccess = () => {
    localStorage.removeItem('googleIdToken');
    setGoogleIdToken(null);
  };

  return (
    <CustomThemeProvider>
      <ErrorProvider>
        <LoadingProvider>
          <NotificationProvider>
            <Router>
              <OrganizationProvider>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/not-authorized" element={<NotAuthorized onSignOutSuccess={handleSignOutSuccess} />} />
                    <Route path="/public/chat/:organizationId/:agentId" element={<PublicChat />} />
                    <Route path="/external/:organizationId/connectionSetup/:connectionId" element={<ExternalConnectionSetup />} />
                    <Route path="/*" element={
                      googleIdToken ? (
                        <Layout
                          googleIdToken={googleIdToken}
                          onSignInSuccess={handleSignInSuccess}
                          onSignOutSuccess={handleSignOutSuccess}
                        >
                          <AppRoutes />
                        </Layout>
                      ) : (
                        <Welcome onSignInSuccess={handleSignInSuccess} />
                      )
                    } />
                  </Routes>
                </Suspense>
                <ErrorDialog />
                <LoadingSpinner />
              </OrganizationProvider>
            </Router>
          </NotificationProvider>
        </LoadingProvider>
      </ErrorProvider>
    </CustomThemeProvider>
  );
}

export default App;
